// src/pages/Level-8.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 8 (V1.1) — Jana Gana Mana
   - Scene video first, then melody page
   - Cloudflare Stream
   - Uses Peacock Hero image
   ───────────────────────────────────────────── */

// ⬇️ Fallback Stream ID (from your screenshot)
const LEVEL8_FALLBACK_STREAM_ID = "740a221ddca0f86126bf121a6b2afdfc";

const LEVEL8_STREAM_ID =
  STREAM_VIDEOS?.L8_ANTHEM_INTRO?.id || LEVEL8_FALLBACK_STREAM_ID;

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL8_VIDEO_SRC = `stream:${LEVEL8_STREAM_ID}`;

// ✅ Hero image (same folder pattern as Level 7)
const LEVEL8_HERO_IMAGE = "/images/" + encodeURIComponent("Peacock_Hero.png");

/* ─────────────────────────────────────────────
   MELODY — Jana Gana Mana
   ✅ Every note is a separate token (NO BD/DE/DF/EE/F#A combos)
   + includes the missing line between old id 7 & id 8
   ───────────────────────────────────────────── */

const LEVEL8_MELODY_LINES = [
  {
    id: 1,
    notes: ["C", "D", "E", "E", "E", "E", "E", "E", "E", "E", "E", "D", "E", "F"],
    lyric: "Jana gana mana adhinayaka jaya he",
  },
  {
    id: 2,
    notes: ["E", "E", "E", "D", "D", "D", "B3", "D", "C"],
    lyric: "Bharata bhagya vidhata",
  },
  {
    id: 3,
    notes: ["C", "G", "G", "G", "G", "G", "G", "G", "G", "G", "F#", "A", "G"],
    lyric: "Panjaba Sindhu Gujarat Maratha",
  },
  {
    id: 4,
    notes: ["F", "F", "F", "E", "E", "E", "D", "F", "E"],
    lyric: "Dravida Utkala Banga",
  },
  {
    id: 5,
    notes: ["E", "E", "E", "E", "E", "D", "G", "G", "G", "F", "F"],
    lyric: "Vindhya Himachala Yamuna Ganga",
  },
  {
    id: 6,
    notes: ["E", "E", "E", "D", "D", "D", "D", "B3", "D", "C"],
    lyric: "Uchchala jaladhi taranga",
  },
  {
    id: 7,
    notes: ["C", "D", "E", "E", "E", "E", "D", "E", "F"],
    lyric: "Tava subha name jage",
  },
  {
    id: 8,
    notes: ["E", "F", "G", "G", "G", "F", "E", "D", "F", "E"],
    lyric: "Tave subha asisa mage",
  },
  {
    id: 9,
    notes: ["E", "E", "D", "D", "D", "D", "B3", "D", "C"],
    lyric: "Gahe tava jaya gatha",
  },
  {
    id: 10,
    notes: ["G", "G", "G", "G", "G", "G", "G", "G", "G", "G", "F#", "A", "G"],
    lyric: "Jana gana mangala dayaka jaya he",
  },
  {
    id: 11,
    notes: ["F", "F", "F", "E", "E", "E", "D", "F", "E"],
    lyric: "Bharata bhagya vidhata",
  },
  {
    id: 12,
    notes: [
      "B",
      "B",
      "C5",
      "B",
      "A",
      "B",
      "G",
      "G",
      "A",
      "C",
      "C",
      "D",
      "D",
      "E",
      "E",
      "D",
      "E",
      "F",
    ],
    lyric: "Jaya he, jaya he, jaya he, jaya jaya jaya jaya he",
  },
];

/* ─────────────────────────────────────────────
   LYRIC GROUPS — per line
   Bracket-style chunking (like you showed)
   ───────────────────────────────────────────── */

const LEVEL8_LYRIC_GROUPS = [
  ["Jana gana mana", "adhinayaka", "jaya he"],
  ["Bha ra ta", "bhag ya vi", "dha ta"],
  ["Panjaba", "Sindhu", "Gujarat", "Maratha"],
  ["Dravida", "Utkala", "Banga"],
  ["Vindhya", "Himachala", "Yamuna", "Ganga"],
  ["Uchchala", "jaladhi", "taranga"],
  ["Tava", "subha", "name", "jage"],
  ["Tave", "subha a", "si sa", "mage"],
  ["Gahe", "tava", "jaya", "gatha"],
  ["Jana gana", "mangala", "dayaka", "jaya he"],
  ["Bha ra ta", "bhag ya vi", "dha ta"],
  ["Jaya he", "jaya he", "jaya he", "jaya jaya", "jaya jaya he"],
];

/* ─────────────────────────────────────────────
   NOTE GROUP INDEXES — per line
   These ranges align notes ⇄ lyric chunks above
   ───────────────────────────────────────────── */

const LEVEL8_NOTE_GROUP_INDEXES = [
  // 1
  [
    { start: 0, end: 2 },
    { start: 3, end: 10 },
    { start: 11, end: 13 },
  ],
  // 2
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 8 },
  ],
  // 3
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 9 },
    { start: 10, end: 12 },
  ],
  // 4
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 8 },
  ],
  // 5
  [
    { start: 0, end: 1 },
    { start: 2, end: 5 },
    { start: 6, end: 8 },
    { start: 9, end: 10 },
  ],
  // 6
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 9 },
  ],
  // 7
  [
    { start: 0, end: 1 },
    { start: 2, end: 3 },
    { start: 4, end: 5 },
    { start: 6, end: 8 },
  ],
  // 8
  [
    { start: 0, end: 1 },
    { start: 2, end: 4 },
    { start: 5, end: 6 },
    { start: 7, end: 9 },
  ],
  // 9
  [
    { start: 0, end: 1 },
    { start: 2, end: 3 },
    { start: 4, end: 5 },
    { start: 6, end: 8 },
  ],
  // 10
  [
    { start: 0, end: 3 },
    { start: 4, end: 7 },
    { start: 8, end: 9 },
    { start: 10, end: 12 },
  ],
  // 11
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 8 },
  ],
  // 12
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 8 },
    { start: 9, end: 12 },
    { start: 13, end: 17 },
  ],
];

/* ─────────────────────────────────────────────
   HERO — Emoji bursts (float + fade + auto-remove)
   - correct: 🦚
   - wrong: 💨
   ───────────────────────────────────────────── */

function Level8Hero({ backgroundImage, noteEvent }) {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(1);

  // Track timeouts so we can clear them on unmount only (NOT on every note)
  const timeoutsRef = useRef([]);

  const safeRand = useMemo(() => {
    const seed = Math.random() * 9999;
    return () => {
      const x = Math.sin(seed + idRef.current * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
  }, []);

  useEffect(() => {
    // cleanup only on unmount
    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!noteEvent) return;

    const isHit = noteEvent === "hit";
    const emoji = isHit ? "🦚" : "💨";

    const id = idRef.current++;
    const left = 18 + safeRand() * 64;
    const top = 18 + safeRand() * 52;

    // slight sideways drift (-14px..+14px)
    const dx = Math.round((safeRand() * 28 - 14) * 10) / 10;

    setBursts((prev) => [...prev, { id, emoji, left, top, isHit, dx }]);

    // Remove after the animation finishes
    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 900);

    timeoutsRef.current.push(t);
  }, [noteEvent, safeRand]);

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      <img
        src={backgroundImage}
        alt=""
        className="w-full h-56 object-cover"
        draggable={false}
      />

      {bursts.map((b) => (
        <div
          key={b.id}
          className="tt-burst"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            "--dx": `${b.dx}px`,
          }}
          aria-hidden="true"
        >
          <span
            className={"tt-burst-emoji " + (b.isHit ? "tt-hit" : "tt-miss")}
          >
            {b.emoji}
          </span>
        </div>
      ))}

      <style>{`
        .tt-burst{
          position:absolute;
          transform: translate(-50%, -50%);
          pointer-events:none;
          z-index: 5;
        }
        .tt-burst-emoji{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size: 34px;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.25));
          animation: ttFloatUp 900ms cubic-bezier(.2,.9,.2,1) forwards;
          will-change: transform, opacity;
        }
        .tt-hit{ }
        .tt-miss{ }

        @keyframes ttFloatUp{
          0%   { transform: translate(0px, 0px) scale(1); opacity: 1; }
          70%  { transform: translate(var(--dx), -26px) scale(1.18); opacity: 1; }
          100% { transform: translate(var(--dx), -46px) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Level8() {
  const navigate = useNavigate();

  return (
    <LevelTemplate
      levelId={8}
      currentLevelForSidebar={8}
      videoSrc={LEVEL8_VIDEO_SRC}
      startLabel="Start Game"
      melodyLines={LEVEL8_MELODY_LINES}
      lyricGroupsPerLine={LEVEL8_LYRIC_GROUPS}
      noteGroupIndexesPerLine={LEVEL8_NOTE_GROUP_INDEXES}
      backgroundImage={LEVEL8_HERO_IMAGE}
      heroImageFit="cover"
      renderHero={(heroProps) => (
        <Level8Hero
          backgroundImage={heroProps.backgroundImage}
          noteEvent={heroProps.noteEvent}
        />
      )}
      completionTitle="Peacock Star unlocked!"
      completionBody={
        <>
          You beautifully played{" "}
          <span className="font-semibold text-[#4F46E5]">Jana Gana Mana</span>{" "}
          🇮🇳🎶 The Peacock bows in pride — collect your{" "}
          <span className="font-semibold text-[#4F46E5]">Peacock Star</span> and
          continue your journey!
        </>
      }
      // ✅ SUCCESS TRIGGER (Level-8 completed + star "L8" collected)
      onComplete={() => {
        TrophyStorage.markLevelAndStar(8); // marks level "8" + star "L8"
      }}
      onNextLevel={() => navigate("/level-9")}
    />
  );
}
